/**
 * What an app must already have before its store art can be generated.
 *
 * ONE definition, shared by the button and the endpoint. The dashboard disables "Generate store
 * assets" until these are present, and the endpoint refuses for the same reason — a gate that only
 * exists in the UI is not a gate, and a gate stated twice is a gate that drifts.
 *
 * ## Why these, and only these
 *
 * **The logo is used.** Every generated panel draws the square mark inside the phone, and the
 * feature graphic puts it on a tile. Without it the render falls back to an initials disc, which is
 * a deliberate fallback for a live emergency — not something to hand an operator as finished store
 * art.
 *
 * **The onboarding images are conditional, and they are a READINESS signal rather than an input.**
 * The generator draws its own app screens; it does not composite `onboard-1..3`. But when a school
 * has the onboarding carousel switched on, those slides are the first thing a new user sees, and an
 * app missing them is not ready to be marketed on a store page. Generating art for it would produce
 * a listing that promises an app that is not finished. When the carousel is OFF they are not
 * required, because then they are not part of the app at all.
 */

import type { MobileAppAssetSlotName } from './mobile-app-asset'

/** Required whatever the app's configuration — the render actually uses it. */
export const STORE_ASSET_REQUIRED_SLOTS = ['logo-small-light'] as const satisfies readonly MobileAppAssetSlotName[]

/** Required only when the onboarding carousel is enabled. */
export const STORE_ASSET_ONBOARDING_SLOTS = [
  'onboard-1',
  'onboard-2',
  'onboard-3'
] as const satisfies readonly MobileAppAssetSlotName[]

/**
 * The slots still missing before store art may be generated. Empty ⇒ go ahead.
 *
 * `filledSlots` is the set of slot names that actually hold an asset — a scaffolded-but-empty row
 * is not filled, which is why callers pass the manifest's populated keys rather than every row.
 */
export function missingStoreAssetPrerequisites(input: {
  filledSlots: readonly string[]
  /** Already resolved through the branding/config precedence; absent decisions mean ON. */
  onboardingEnabled: boolean
}): MobileAppAssetSlotName[] {
  const filled = new Set(input.filledSlots)
  const required: readonly MobileAppAssetSlotName[] = input.onboardingEnabled
    ? [...STORE_ASSET_REQUIRED_SLOTS, ...STORE_ASSET_ONBOARDING_SLOTS]
    : STORE_ASSET_REQUIRED_SLOTS
  return required.filter(slot => !filled.has(slot))
}

/** The refusal in operator words, naming what to upload and where the rule comes from. */
export function storeAssetPrerequisiteMessage(missing: readonly MobileAppAssetSlotName[]): string {
  const onboarding = missing.filter(slot => (STORE_ASSET_ONBOARDING_SLOTS as readonly string[]).includes(slot))
  const parts: string[] = []
  if (missing.some(slot => (STORE_ASSET_REQUIRED_SLOTS as readonly string[]).includes(slot))) {
    parts.push('the square Logo')
  }
  if (onboarding.length > 0) {
    parts.push(`${onboarding.length} onboarding image${onboarding.length === 1 ? '' : 's'} (the carousel is on)`)
  }
  return `Upload ${parts.join(' and ')} first — store art is generated from them.`
}
